import React, { useEffect } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';

import { useDeleteUser } from '@SERVICES';

import useDeleteUserModal from '@HOOKS/store/modals/useDeleteUserModal';

import BaseModal from '@MODALS/BaseModal';
import SimpleLargeModalHeader from '@COMPONENTS/SHARED/SimpleLargeModalHeader';

import styles from './DeleteUserModal.module.scss';

const cx: CX = classnames.bind(styles);

function DeleteUserModal() {
    const {
        state: { isOpen, data: userData },
        actions: deleteUserModalActions,
    } = useDeleteUserModal(() => true);

    const [
        deleteUser,
        {
            isLoading, isSuccess, isError, error,
        },
    ] = useDeleteUser();

    useEffect(() => {
        if (isSuccess) {
            notify.info('User has been deleted successfully', {
                toastId: 'delete-user-success',
            });

            deleteUserModalActions.close();
        }
    }, [isSuccess, deleteUserModalActions]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'delete-user-permission-error',
                });
            } else {
                notify.error('Failed to delete user', {
                    toastId: 'delete-user-error',
                });
            }
        }
    }, [isError, error]);

    const handleConfirm = () => {
        if (userData) {
            deleteUser(userData.id);
            deleteUserModalActions.confirm();
        }
    };

    const handleClose = () => {
        deleteUserModalActions.close();
    };

    if (!isOpen || !userData) {
        return null;
    }

    return (
        <BaseModal onClose={handleClose}>
            <div className={cx('delete-user-modal')}>
                <SimpleLargeModalHeader
                    modalTitle="Delete User"
                    title=""
                    onClose={handleClose}
                />
                <div className={cx('content')}>
                    <div className={cx('warning-text')}>
                        Are you sure you want to delete this user?
                    </div>
                    <div className={cx('user-info')}>
                        <div className={cx('user-name')}>
                            <strong>{userData.fullName}</strong>
                        </div>
                        <div className={cx('user-email')}>
                            {userData.email}
                        </div>
                        {userData.title && (
                            <div className={cx('user-title')}>
                                {userData.title}
                            </div>
                        )}
                    </div>
                    <div className={cx('warning-note')}>
                        <strong>Warning:</strong>
                        {' '}
                        This action cannot be undone. The user will lose access to the system immediately and all their data associations will be removed.
                    </div>
                    <div className={cx('buttons')}>
                        <button
                            type="button"
                            className={cx('cancel-button')}
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={cx('delete-button')}
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Deleting...' : 'Delete User'}
                        </button>
                    </div>
                </div>
            </div>
        </BaseModal>
    );
}

export default DeleteUserModal; 